import { type ReactNode, createContext, createElement, useContext, useMemo } from "react";
import biomelLogo from "../assets/partner-logos/biomel.png?inline";
import heightsLogo from "../assets/partner-logos/heights.png?inline";
import lociLogo from "../assets/partner-logos/loci.png?inline";
import neutonicLogo from "../assets/partner-logos/neutonic.png?inline";
import salkanLogo from "../assets/partner-logos/salkan.png?inline";
import underwayLogo from "../assets/partner-logos/underway.png?inline";
import { readHostString } from "./host-props";

export type TLandingPartnerLogo = {
	href?: string;
	name: string;
	src: string;
};

/** Hardcoded partner logos bundled with the landing page. */
export const DEFAULT_LANDING_PARTNER_LOGOS: readonly TLandingPartnerLogo[] = [
	{ name: "Neutonic", src: neutonicLogo },
	{ name: "Heights", src: heightsLogo },
	{ name: "Underway", src: underwayLogo },
	{ name: "Loci", src: lociLogo },
	{ name: "Biomel", src: biomelLogo },
	{ name: "SALKAN", src: salkanLogo },
];

export function parsePartnerLogos(value: unknown): TLandingPartnerLogo[] | undefined {
	if (!Array.isArray(value)) {
		return undefined;
	}

	const logos: TLandingPartnerLogo[] = [];
	for (const entry of value) {
		if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
			continue;
		}
		const record = entry as Record<string, unknown>;
		const src = readHostString(record.src);
		const name = readHostString(record.name);
		if (!src || !name) {
			continue;
		}
		const href = readHostString(record.href);
		logos.push(href ? { href, name, src } : { name, src });
	}

	return logos.length > 0 ? logos : undefined;
}

export type TPartnerLogoNode = {
	alt: string | null;
	href: string | null;
	label: string | null;
	src: string | null;
};

export function partnerLogosFromNodes(nodes: readonly TPartnerLogoNode[]): TLandingPartnerLogo[] | undefined {
	const logos: TLandingPartnerLogo[] = [];
	for (const node of nodes) {
		const src = readHostString(node.src);
		if (!src) {
			continue;
		}
		const name = readHostString(node.alt) ?? readHostString(node.label) ?? "Customer";
		const href = readHostString(node.href);
		logos.push(href ? { href, name, src } : { name, src });
	}
	return logos.length > 0 ? logos : undefined;
}

export function mergeLandingPartnerLogos(overrides?: readonly TLandingPartnerLogo[]): TLandingPartnerLogo[] {
	return overrides && overrides.length > 0 ? [...overrides] : [...DEFAULT_LANDING_PARTNER_LOGOS];
}

const LandingPartnerLogosContext = createContext<TLandingPartnerLogo[] | null>(null);

export function LandingPartnerLogosProvider({
	logos,
	children,
}: {
	logos?: readonly TLandingPartnerLogo[];
	children: ReactNode;
}) {
	const value = mergeLandingPartnerLogos(logos);
	return createElement(LandingPartnerLogosContext.Provider, { value }, children);
}

export function useLandingPartnerLogos(): TLandingPartnerLogo[] {
	const provided = useContext(LandingPartnerLogosContext);
	return useMemo(() => provided ?? [...DEFAULT_LANDING_PARTNER_LOGOS], [provided]);
}
